'use client';

import { useTranslation } from 'next-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/solid';
import { StoryCard } from '@components/templates/homepage/StoryCard';
import { homepageStories } from '@components/templates/stories/homepageStories';
import { useState, useEffect, useRef } from 'react';

export const StoriesSection = () => {
  // Randomize stories list
  const [randomizedStories, setRandomizedStories] = useState<ApiSchema<'ResumeStoryCardData'>[]>([]);

  const currentlyPlayingRef = useRef<HTMLVideoElement | null>(null);

  /** Pauses the currently playing video (if any) before allowing a new one to play */
  const handlePlayStart = (videoElement: HTMLVideoElement) => {
    if (currentlyPlayingRef.current && currentlyPlayingRef.current !== videoElement) {
      currentlyPlayingRef.current.pause();
    }
    currentlyPlayingRef.current = videoElement;
  };

  useEffect(() => {
    setRandomizedStories([...homepageStories].sort(() => Math.random() - 0.5));
  }, []);

  const { t } = useTranslation('common');

  return (
    <div className="w-full">
      {/* Header Section with Navigation */}
      <div className="mb-6 flex flex-row items-center justify-between gap-4">
        {/* Column 1: Title and Subtitle */}
        <div className="flex flex-col">
          <p className="mt-2 text-lg">
            {t('homepage.stories.subtitle')}
          </p>
        </div>

        {/* Column 2: Navigation Arrows */}
        <div className="flex shrink-0 items-center gap-3">
          <button
            type="button"
            className="stories-prev-btn flex size-6 items-center justify-center rounded-full bg-transparent text-ink shadow-lg transition-all hover:scale-110 md:size-8"
            aria-label="Previous story"
          >
            <ArrowLeftIcon className="size-4 md:size-5" />
          </button>
          <button
            type="button"
            className="stories-next-btn flex size-6 items-center justify-center rounded-full bg-transparent text-ink shadow-lg transition-all hover:scale-110 md:size-8"
            aria-label="Next story"
          >
            <ArrowRightIcon className="size-4 md:size-5" />
          </button>
        </div>
      </div>

      {/* Stories Carousel */}
      <div className="relative pr-1 sm:pr-2 md:pr-3">
        <Swiper
          modules={[Navigation]}
          navigation={{
            nextEl: '.stories-next-btn',
            prevEl: '.stories-prev-btn',
          }}
          slidesPerView={1.3}
          spaceBetween={16}
          slidesOffsetAfter={32}
          breakpoints={{
            640: {
              slidesPerView: 2.5,
              spaceBetween: 14,
              slidesOffsetAfter: 28,
            },
            1024: {
              slidesPerView: 5,
              spaceBetween: 16,
              slidesOffsetAfter: 32,
            },
          }}
          className="!pb-4"
        >
          {randomizedStories.length > 0 && randomizedStories.map((story) => (
            <SwiperSlide key={story.id}>
              <StoryCard story={story} onPlayStart={handlePlayStart} />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
};
